import type { WiseguyAlert } from '../../types/wiseguy';

/**
 * Cell formatters for the Wiseguy tape, shared by the table and the row
 * detail modal.
 *
 * ⚠️ Lifted out of `WiseguyAlertsWidget` so the modal can reuse them. It
 * cannot import from the widget — the widget imports the modal, and that
 * round trip is a cycle — and a second copy of these rules would drift from
 * the table the first time one side was tweaked.
 */

/** Display formatters matching QF board rules (field map §6). */
export function formatIv(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '-';
  const pct = Math.abs(n) <= 2 ? n * 100 : n;
  return `${pct.toFixed(1)}%`;
}

export function formatIvChgPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '-';
  const pct = Math.abs(n) <= 2 ? n * 100 : n;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

export function formatDelta(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '-';
  const points = Math.abs(n) <= 1 ? n * 100 : n;
  return points.toFixed(0);
}

export function formatQty(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '-';
  return n.toLocaleString('en-US');
}

export function formatOi(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '-';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function formatFlag(flag: string | null | undefined): string {
  if (!flag) return '-';
  return flag.charAt(0).toUpperCase() + flag.slice(1);
}

/**
 * ⚠️ **DAVE — C/P should show the SIDE too, and cannot yet.**
 *
 * The column renders `C` / `P` from `contract_type`, which is only
 * `Call | Put` — the instrument, not who took which side. What is wanted is:
 *
 *   C  = call buyer   (bullish, blue)      CS = call seller (bearish, red)
 *   P  = put buyer    (bearish, red)       PS = put seller  (bullish, blue)
 *
 * Note both seller cases INVERT the colour: a call seller is bearish and a put
 * seller is bullish, so the badge must follow the direction of the position
 * rather than the instrument.
 *
 * **This needs a definitive buy/sell field on the alert.** It was deliberately
 * NOT inferred from `aggressor_class` / `side_descriptor` (the 5-class NBBO
 * fill-location label: "Below Bid" | "Bid Side" | "MidMkt" | "Ask Side" |
 * "Above Ask"). Reading bid-side as "sold" is a guess about intent from where a
 * print landed, and "MidMkt"/empty/missing would have to fall through to
 * "buyer" — silently printing `C` for an alert whose side is unknown. A wrong
 * direction that looks confident is worse than no seller flag at all.
 */
export function formatSide(alert: WiseguyAlert): string {
  if (alert.aggressor_class) return alert.aggressor_class;
  if (alert.side_descriptor) return alert.side_descriptor.replace(/_/g, ' ');
  return '-';
}

export function formatLast(last: string | null | undefined): string {
  if (last == null || last === '') return '-';
  const n = parseFloat(last);
  if (Number.isNaN(n)) return last;
  return n.toFixed(2);
}

/**
 * Single Condition column: trade condition tokens + event tags (Pre-Earnings, etc.).
 * Events is not a separate UI column.
 */
export function formatConditionCombined(alert: WiseguyAlert): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const raw of [alert.condition, alert.events]) {
    if (!raw) continue;
    for (const token of raw.split(',').map((t) => t.trim()).filter(Boolean)) {
      const key = token.toUpperCase();
      if (seen.has(key)) continue;
      seen.add(key);
      parts.push(token);
    }
  }
  return parts.length > 0 ? parts.join(', ') : '-';
}
